import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { Page } from '@/components/Page';
import { Section } from '@/components/Section';
import { Cell } from '@/components/Cell';
import { Empty } from '@/components/Empty';
import { Spinner } from '@/components/Spinner';
import { Input } from '@/components/Input';
import { useSupabaseQuery, sb } from '@/hooks/useSupabaseQuery';
import { useAuth } from '@/providers/AuthProvider';
import { useHaptic } from '@/hooks/useHaptic';
import { fmtDateTime } from '@/lib/format';
import { listUnderlyings } from '@/lib/listUnderlyings';
import { fetchOptionsSnapshot, type OptionTickerRow, type OptionsSnapshot } from '@/lib/optionsSnapshot';
import type { GammaAlert, GammaCheckHistory, UserSettings } from '@/types/domain';

const POSITIONS_REFRESH_MS = 60_000;

function fmtValue(v: number | null | undefined, zeroAsDash = false): string {
  if (v == null) return '—';
  if (zeroAsDash && v === 0) return '—';
  return v.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 6 });
}

interface LatestQuote {
  bid: number;
  ask: number;
  bidSize: number | null;
  askSize: number | null;
}

interface BatchOrderState {
  status: 'pending' | 'success' | 'error';
  message: string;
}

interface PositionInfo {
  qty: number;
  avgEntryPrice: number | null;
}

const BULK_SELECT_WINDOW_MS = 3 * 60 * 1000;
/** Если последняя проверка старше этого порога — poller-options/worker не пишет историю. */
const STALE_HISTORY_MS = 5 * 60 * 1000;
const PREFILLED_ORDER_FACTOR = 0.9;
const ORDER_PRICE_DISCOUNT_FACTOR = 0.95;

/** Округление цены до 8 знаков — убирает float-шум вроде 0.09500000000000001. */
function roundPrice(v: number): number {
  return Math.round(v * 1e8) / 1e8;
}

function positiveOrNull(v: unknown): number | null {
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function pickOrderQty(myQty: number | null, bookQty: number | null): number | null {
  if (myQty == null || bookQty == null) return null;
  const qty = Math.min(myQty, bookQty);
  return Number.isFinite(qty) && qty > 0 ? qty : null;
}

function parseSymbolExpiry(symbol: string): Date | null {
  const raw = symbol.split('-')[1] ?? '';
  if (!/^\d{6,8}$/.test(raw)) return null;
  const ymd = raw.length === 8 ? raw : `20${raw}`;
  const y = Number(ymd.slice(0, 4));
  const m = Number(ymd.slice(4, 6));
  const d = Number(ymd.slice(6, 8));
  if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) return null;
  return new Date(Date.UTC(y, m - 1, d));
}

function isTodayOrTomorrow(expiry: Date, now: Date): boolean {
  const start = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  const end = start + 2 * 24 * 60 * 60 * 1000;
  const value = expiry.getTime();
  return value >= start && value < end;
}

function symbolUnderlying(symbol: string): string {
  const head = symbol.split('-')[0]?.toUpperCase().replaceAll('_', '') ?? '';
  return head.endsWith('USDT') ? head : `${head}USDT`;
}

/** Раздел Gamma. Срабатывания «bid >= N × ask за 10 минут» и история проверок. */
export function GammaPage(): JSX.Element {
  const { userId } = useAuth();
  const { notify } = useHaptic();

  const [tab, setTab] = useState<'alerts' | 'history' | 'snapshot'>('alerts');
  const [underlyings, setUnderlyings] = useState<string[]>([]);
  const [copiedTicker, setCopiedTicker] = useState<string | null>(null);
  const [snapshotUnderlying, setSnapshotUnderlying] = useState<string | null>(null);
  const [snapshotData, setSnapshotData] = useState<OptionsSnapshot | null>(null);
  const [snapshotExpiry, setSnapshotExpiry] = useState<string | null>(null);
  const [snapshotLoading, setSnapshotLoading] = useState(false);
  const [snapshotError, setSnapshotError] = useState<string | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(false);

  // Настройки gamma_ratio
  const [ratioStr, setRatioStr] = useState('2.1');
  const [ratioLoading, setRatioLoading] = useState(true);
  const [ratioError, setRatioError] = useState<string | null>(null);
  // Минимальный bid (USDT) для Gamma-алерта; 0 — без фильтра.
  const [minBidStr, setMinBidStr] = useState('0');
  const [minBidError, setMinBidError] = useState<string | null>(null);
  const [selectedAlert, setSelectedAlert] = useState<GammaAlert | null>(null);
  const [orderSide, setOrderSide] = useState<'bid' | 'ask'>('bid');
  const [orderQty, setOrderQty] = useState('0');
  const [orderPrice, setOrderPrice] = useState('0');
  const [orderLoading, setOrderLoading] = useState(false);
  const [orderResult, setOrderResult] = useState<string | null>(null);
  const [orderError, setOrderError] = useState<string | null>(null);
  const [latestQuote, setLatestQuote] = useState<LatestQuote | null>(null);
  const [excludeNearExpiry, setExcludeNearExpiry] = useState(false);
  const [disabledUnderlyings, setDisabledUnderlyings] = useState<string[]>([]);
  const disabledUnderlyingsRef = useRef<string[]>([]);
  const [selectedAlertIds, setSelectedAlertIds] = useState<Set<number>>(new Set());
  const [batchOrderState, setBatchOrderState] = useState<Record<number, BatchOrderState>>({});
  const [batchOrdering, setBatchOrdering] = useState(false);
  const batchOrderStateRef = useRef<Record<number, BatchOrderState>>({});
  const [positionsBySymbol, setPositionsBySymbol] = useState<Record<string, PositionInfo>>({});
  const [prefsError, setPrefsError] = useState<string | null>(null);
  // Счётчики запросов — ответы от устаревших запросов игнорируются.
  const quoteReqIdRef = useRef(0);
  const positionsReqIdRef = useRef(0);
  const snapshotReqIdRef = useRef(0);
  // `${underlying}|${expiry}` уже загруженного снапшота — чтобы не запрашивать его повторно.
  const skipSnapshotFetchRef = useRef<string | null>(null);

  useEffect(() => {
    void listUnderlyings().then(setUnderlyings);
  }, []);

  useEffect(() => {
    if (!userId) {
      setRatioLoading(false);
      return;
    }
    void sb
      .from('user_settings')
      .select('gamma_ratio, gamma_min_bid, gamma_exclude_near_expiry, gamma_disabled_underlyings')
      .eq('user_id', userId)
      .maybeSingle()
      .then(({ data, error }) => {
        setRatioLoading(false);
        if (error) {
          console.error('[GammaPage] load settings error:', error.message);
          setPrefsError(`Не удалось загрузить настройки: ${error.message}`);
          return;
        }
        const s = data as Pick<UserSettings, 'gamma_ratio' | 'gamma_min_bid' | 'gamma_exclude_near_expiry' | 'gamma_disabled_underlyings'> | null;
        if (s?.gamma_ratio != null) {
          setRatioStr(String(s.gamma_ratio));
        }
        if (s?.gamma_min_bid != null) {
          setMinBidStr(String(Number(s.gamma_min_bid)));
        }
        setExcludeNearExpiry(Boolean(s?.gamma_exclude_near_expiry));
        const disabled = Array.isArray(s?.gamma_disabled_underlyings)
          ? s.gamma_disabled_underlyings.filter((v): v is string => typeof v === 'string')
          : [];
        disabledUnderlyingsRef.current = disabled;
        setDisabledUnderlyings(disabled);
      });
  }, [userId]);

  const saveGammaPrefs = useCallback(async (payload: {
    gamma_exclude_near_expiry?: boolean;
    gamma_disabled_underlyings?: string[];
  }): Promise<boolean> => {
    if (!userId) {
      setPrefsError('Не авторизован. Откройте приложение через Telegram.');
      notify('error');
      return false;
    }
    setPrefsError(null);
    const { error } = await sb.from('user_settings').upsert(
      { user_id: userId, ...payload, updated_at: new Date().toISOString() },
      { onConflict: 'user_id' },
    );
    if (error) {
      console.error('[GammaPage] save prefs error:', error.message);
      setPrefsError(`Не удалось сохранить: ${error.message}`);
      notify('error');
      return false;
    }
    return true;
  }, [userId, notify]);

  const saveRatio = useCallback(async (): Promise<void> => {
    if (!userId) return;
    const normalized = ratioStr.replace(',', '.').trim();
    const v = Number(normalized);
    if (!Number.isFinite(v) || v < 1.0 || v > 100.0) {
      setRatioError('Используйте точку или запятую, диапазон 1.0–100.0');
      notify('error');
      return;
    }
    setRatioError(null);
    const { error } = await sb.from('user_settings').upsert(
      { user_id: userId, gamma_ratio: v, updated_at: new Date().toISOString() },
      { onConflict: 'user_id' },
    );
    if (error) {
      setRatioError(`Не удалось сохранить: ${error.message}`);
      notify('error');
    } else {
      setRatioStr(String(v));
      notify('success');
    }
  }, [userId, ratioStr, notify]);

  const saveMinBid = useCallback(async (): Promise<void> => {
    if (!userId) return;
    const normalized = minBidStr.replace(/,/g, '.').trim();
    const v = normalized === '' ? 0 : Number(normalized);
    if (!Number.isFinite(v) || v < 0) {
      setMinBidError('Введите число ≥ 0 (точка или запятая), 0 — без фильтра');
      notify('error');
      return;
    }
    setMinBidError(null);
    const { error } = await sb.from('user_settings').upsert(
      { user_id: userId, gamma_min_bid: v, updated_at: new Date().toISOString() },
      { onConflict: 'user_id' },
    );
    if (error) {
      setMinBidError(`Не удалось сохранить: ${error.message}`);
      notify('error');
    } else {
      setMinBidStr(String(v));
      notify('success');
    }
  }, [userId, minBidStr, notify]);

  const copyTicker = (ticker: string): void => {
    const showFeedback = (): void => {
      setCopiedTicker(ticker);
      setTimeout(() => setCopiedTicker(null), 1500);
    };
    void navigator.clipboard.writeText(ticker).then(showFeedback).catch(showFeedback);
  };

  const alerts = useSupabaseQuery<GammaAlert[]>(
    () => sb
      .from('gamma_alerts')
      .select('*')
      .gte('created_at', new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString())
      .order('created_at', { ascending: false })
      .limit(100),
    [],
    10_000,
  );

  // Без фильтра «за 24 часа»: если фоновая проверка остановилась, показываем
  // последние записи с предупреждением, а не пустой список.
  const history = useSupabaseQuery<GammaCheckHistory[]>(
    () =>
      tab === 'history'
        ? sb
            .from('gamma_check_history')
            .select('*')
            .order('checked_at', { ascending: false })
            .limit(200)
        : Promise.resolve({ data: null, error: null }),
    [tab],
    30_000,
  );
  // Время последней проверки — для предупреждения об остановленном poller-options/worker.
  const latestCheck = useSupabaseQuery<Pick<GammaCheckHistory, 'checked_at'>[]>(
    () => sb
      .from('gamma_check_history')
      .select('checked_at')
      .order('checked_at', { ascending: false })
      .limit(1),
    [],
    30_000,
  );
  const lastCheckAt = latestCheck.data?.[0]?.checked_at ?? null;
  const historyAgeMs = lastCheckAt ? Date.now() - new Date(lastCheckAt).getTime() : 0;
  const historyStale = !!lastCheckAt && historyAgeMs > STALE_HISTORY_MS;

  const loadLatestQuote = useCallback(async (symbol: string): Promise<void> => {
    const reqId = ++quoteReqIdRef.current;
    const { data, error } = await sb
      .from('options_quotes_flat')
      .select('bid, ask, bid_size, ask_size')
      .eq('symbol', symbol)
      .order('taken_at', { ascending: false })
      .limit(1)
      .maybeSingle();
    if (reqId !== quoteReqIdRef.current) return;
    if (error) {
      setLatestQuote(null);
      return;
    }
    const row = data as { bid: number; ask: number; bid_size: number | null; ask_size: number | null } | null;
    if (!row) {
      setLatestQuote(null);
      return;
    }
    setLatestQuote({
      bid: Number(row.bid),
      ask: Number(row.ask),
      bidSize: positiveOrNull(row.bid_size),
      askSize: positiveOrNull(row.ask_size),
    });
  }, []);

  const loadPositions = useCallback(async (symbols: string[]): Promise<void> => {
    const reqId = ++positionsReqIdRef.current;
    const uniqueSymbols = [...new Set(symbols.filter(Boolean))];
    if (uniqueSymbols.length === 0) {
      setPositionsBySymbol({});
      return;
    }
    const { data, error } = await sb.functions.invoke('gate-options-positions', {
      body: { symbols: uniqueSymbols },
    });
    if (reqId !== positionsReqIdRef.current) return;
    if (error || !data || typeof data !== 'object' || !('positions' in data)) {
      setPositionsBySymbol({});
      return;
    }
    const rows = (data as { positions?: Array<{ symbol?: string; qty?: number; avgEntryPrice?: number | null }> }).positions ?? [];
    const next: Record<string, PositionInfo> = {};
    for (const row of rows) {
      const symbol = typeof row.symbol === 'string' ? row.symbol : '';
      const qty = positiveOrNull(row.qty);
      if (!symbol || qty == null) continue;
      next[symbol] = { qty, avgEntryPrice: positiveOrNull(row.avgEntryPrice) };
    }
    setPositionsBySymbol(next);
  }, []);

  // Стабильный ключ набора символов: поллинг алертов каждые 10 сек создаёт новый
  // массив, но edge-функцию позиций дёргаем только при изменении набора символов.
  const alertSymbolsKey = useMemo(
    () => [...new Set((alerts.data ?? []).map((a) => a.symbol).filter(Boolean))].sort().join(','),
    [alerts.data],
  );

  useEffect(() => {
    const symbols = alertSymbolsKey ? alertSymbolsKey.split(',') : [];
    void loadPositions(symbols);
    if (symbols.length === 0) return undefined;
    // Позиции меняются и без смены набора символов (покупка/продажа на бирже) —
    // обновляем раз в минуту, пока вкладка видима.
    const timer = setInterval(() => {
      if (!document.hidden) void loadPositions(symbols);
    }, POSITIONS_REFRESH_MS);
    return () => clearInterval(timer);
  }, [alertSymbolsKey, loadPositions]);

  const openOrderModal = useCallback((alert: GammaAlert): void => {
    const myQty = positionsBySymbol[alert.symbol]?.qty ?? null;
    const bidBookQty = positiveOrNull(alert.best_bid_size);
    const defaultQty = pickOrderQty(myQty, bidBookQty);
    const defaultPrice = positiveOrNull(alert.new_bid);
    setSelectedAlert(alert);
    setOrderSide('bid');
    setOrderQty(defaultQty != null ? String(defaultQty) : '0');
    setOrderPrice(defaultPrice != null ? String(roundPrice(defaultPrice * ORDER_PRICE_DISCOUNT_FACTOR)) : '0');
    setOrderError(null);
    setOrderResult(null);
    setLatestQuote(null);
    void loadLatestQuote(alert.symbol);
  }, [loadLatestQuote, positionsBySymbol]);

  const submitOrder = useCallback(async (): Promise<void> => {
    if (!selectedAlert) return;
    const qty = Number(orderQty.replace(',', '.'));
    const price = Number(orderPrice.replace(',', '.'));
    if (!Number.isFinite(qty) || qty <= 0) {
      setOrderError('Количество должно быть больше 0');
      notify('error');
      return;
    }
    if (!Number.isFinite(price) || price <= 0) {
      setOrderError('Цена должна быть больше 0');
      notify('error');
      return;
    }
    setOrderLoading(true);
    setOrderError(null);
    setOrderResult(null);
    const { data, error } = await sb.functions.invoke('gate-place-limit-order', {
      body: {
        symbol: selectedAlert.symbol,
        side: orderSide,
        quantity: qty,
        price,
      },
    });
    setOrderLoading(false);
    if (error || (data && typeof data === 'object' && 'error' in data && data.error)) {
      const message = (data && typeof data === 'object' && 'message' in data && typeof data.message === 'string')
        ? data.message
        : (error?.message ?? 'Не удалось отправить лимитный ордер');
      setOrderError(message);
      notify('error');
      return;
    }
    const orderId = data && typeof data === 'object' && 'order_id' in data ? String(data.order_id) : '—';
    setOrderResult(`Лимитный ордер отправлен. ID: ${orderId}, цена: ${price.toFixed(6)}`);
    notify('success');
  }, [selectedAlert, orderQty, orderPrice, orderSide, notify]);

  const toggleExcludeNearExpiry = useCallback((enabled: boolean): void => {
    setExcludeNearExpiry(enabled);
    void saveGammaPrefs({ gamma_exclude_near_expiry: enabled }).then((ok) => {
      if (!ok) setExcludeNearExpiry(!enabled);
    });
  }, [saveGammaPrefs]);

  const setUnderlyingEnabled = useCallback((ticker: string, enabled: boolean): void => {
    const prev = disabledUnderlyingsRef.current;
    const next = enabled ? prev.filter((v) => v !== ticker) : [...new Set([...prev, ticker])];
    disabledUnderlyingsRef.current = next;
    setDisabledUnderlyings(next);
    void saveGammaPrefs({ gamma_disabled_underlyings: next }).then((ok) => {
      if (!ok && disabledUnderlyingsRef.current === next) {
        disabledUnderlyingsRef.current = prev;
        setDisabledUnderlyings(prev);
      }
    });
  }, [saveGammaPrefs]);

  const filteredAlerts = useMemo(() => {
    const now = new Date();
    const disabled = new Set(disabledUnderlyings);
    return (alerts.data ?? []).filter((a) => {
      if (disabled.has(symbolUnderlying(a.symbol))) return false;
      if (!excludeNearExpiry) return true;
      const expiry = parseSymbolExpiry(a.symbol);
      return !expiry || !isTodayOrTomorrow(expiry, now);
    });
  }, [alerts.data, excludeNearExpiry, disabledUnderlyings]);

  useEffect(() => {
    setSelectedAlertIds((prev) => {
      const allowed = new Set(filteredAlerts.map((a) => a.id));
      const next = new Set([...prev].filter((id) => allowed.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [filteredAlerts]);

  useEffect(() => {
    batchOrderStateRef.current = batchOrderState;
  }, [batchOrderState]);

  const toggleAlertSelection = useCallback((id: number): void => {
    setSelectedAlertIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const selectLast3Minutes = useCallback((): void => {
    const since = Date.now() - BULK_SELECT_WINDOW_MS;
    const ids = filteredAlerts
      .filter((a) => Date.parse(a.created_at) >= since)
      .map((a) => a.id);
    setSelectedAlertIds(new Set(ids));
    setBatchOrderState({});
  }, [filteredAlerts]);

  const placePreFilledOrder = useCallback(async (alert: GammaAlert): Promise<void> => {
    if (batchOrderStateRef.current[alert.id]?.status === 'pending') return;
    const myQty = positionsBySymbol[alert.symbol]?.qty ?? null;
    const bookQty = positiveOrNull(alert.best_bid_size);
    const quantity = pickOrderQty(myQty, bookQty);
    const price = roundPrice(Number(alert.new_bid) * PREFILLED_ORDER_FACTOR);
    if (quantity == null || !Number.isFinite(price) || price <= 0) {
      setBatchOrderState((prev) => ({ ...prev, [alert.id]: { status: 'error', message: 'Нет корректного bid qty/price' } }));
      return;
    }
    // Ref обновляем синхронно — иначе двойной тап успеет пройти guard до эффекта синхронизации.
    batchOrderStateRef.current = {
      ...batchOrderStateRef.current,
      [alert.id]: { status: 'pending', message: 'Отправка…' },
    };
    setBatchOrderState((prev) => ({ ...prev, [alert.id]: { status: 'pending', message: 'Отправка…' } }));
    const { data, error } = await sb.functions.invoke('gate-place-limit-order', {
      body: { symbol: alert.symbol, side: 'bid', quantity, price },
    });
    if (error || (data && typeof data === 'object' && 'error' in data && data.error)) {
      const message = (data && typeof data === 'object' && 'message' in data && typeof data.message === 'string')
        ? data.message
        : (error?.message ?? 'Ошибка отправки');
      setBatchOrderState((prev) => ({ ...prev, [alert.id]: { status: 'error', message } }));
      return;
    }
    const orderId = data && typeof data === 'object' && 'order_id' in data ? String(data.order_id) : '—';
    setBatchOrderState((prev) => ({ ...prev, [alert.id]: { status: 'success', message: `OK, ID: ${orderId}` } }));
  }, [positionsBySymbol]);

  const submitSelectedOrders = useCallback(async (): Promise<void> => {
    const selected = filteredAlerts.filter((a) => selectedAlertIds.has(a.id));
    if (selected.length === 0) return;
    setBatchOrdering(true);
    for (const alert of selected) {
      // eslint-disable-next-line no-await-in-loop
      await placePreFilledOrder(alert);
    }
    setBatchOrdering(false);
  }, [filteredAlerts, selectedAlertIds, placePreFilledOrder]);

  // Возвращает null и ничего не пишет в state, если за время запроса стартовал более новый.
  const loadSnapshot = useCallback(async (underlying: string, expiry?: string): Promise<OptionsSnapshot | null> => {
    const reqId = ++snapshotReqIdRef.current;
    setSnapshotLoading(true);
    setSnapshotError(null);
    try {
      const data = await fetchOptionsSnapshot(underlying, expiry);
      if (reqId !== snapshotReqIdRef.current) return null;
      setSnapshotData(data);
      return data;
    } catch (err) {
      if (reqId !== snapshotReqIdRef.current) return null;
      const message = err instanceof Error ? err.message : 'Не удалось получить снапшот';
      setSnapshotError(message);
      return null;
    } finally {
      if (reqId === snapshotReqIdRef.current) setSnapshotLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!snapshotUnderlying) return;
    let cancelled = false;
    const run = async (): Promise<void> => {
      // Без экспирации options-snapshot сам выбирает первую доступную и возвращает её срез —
      // достаточно одного запроса. Старая версия функции отдаёт selected_expiry = null —
      // тогда догружаем первую экспирацию отдельным запросом.
      const data = await loadSnapshot(snapshotUnderlying);
      if (!data || cancelled) return;
      const selected = data.selected_expiry && data.expiries.includes(data.selected_expiry)
        ? data.selected_expiry
        : null;
      const nextExpiry = selected ?? data.expiries[0] ?? null;
      skipSnapshotFetchRef.current = selected ? `${snapshotUnderlying}|${selected}` : null;
      setSnapshotExpiry(nextExpiry);
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [snapshotUnderlying, loadSnapshot]);

  useEffect(() => {
    if (!snapshotUnderlying || !snapshotExpiry) return;
    const key = `${snapshotUnderlying}|${snapshotExpiry}`;
    const skip = skipSnapshotFetchRef.current === key;
    skipSnapshotFetchRef.current = null;
    if (skip) return;
    void loadSnapshot(snapshotUnderlying, snapshotExpiry);
  }, [snapshotUnderlying, snapshotExpiry, loadSnapshot]);

  useEffect(() => {
    if (!autoRefresh || tab !== 'snapshot' || !snapshotUnderlying || !snapshotExpiry) return;
    const id = setInterval(() => {
      void loadSnapshot(snapshotUnderlying, snapshotExpiry);
    }, 10_000);
    return () => clearInterval(id);
  }, [autoRefresh, tab, snapshotUnderlying, snapshotExpiry, loadSnapshot]);

  const refreshSnapshot = useCallback((): void => {
    if (!snapshotUnderlying || !snapshotExpiry) return;
    void loadSnapshot(snapshotUnderlying, snapshotExpiry);
  }, [snapshotUnderlying, snapshotExpiry, loadSnapshot]);

  const strikeRows = useMemo(() => {
    const groupedByStrike = new Map<number, { call: OptionTickerRow | null; put: OptionTickerRow | null }>();
    for (const row of snapshotData?.rows ?? []) {
      let current = groupedByStrike.get(row.strike);
      if (!current) {
        current = { call: null, put: null };
        groupedByStrike.set(row.strike, current);
      }
      if (row.side === 'C') current.call = row;
      if (row.side === 'P') current.put = row;
    }
    return [...groupedByStrike.entries()]
      .sort((a, b) => a[0] - b[0])
      .map(([strike, pair]) => ({ strike, ...pair }));
  }, [snapshotData?.rows]);

  return (
    <Page title="Gamma — опционы">
      <Section
        header="Настройки порога"
        footer={`Алерт срабатывает, когда bid ≥ N × ask за последние 10 минут и bid ≥ мин. bid. Можно менять без перезагрузки.`}
      >
        {ratioLoading ? (
          <Spinner />
        ) : (
          <>
            <Input
              label="Порог bid/ask (×)"
              type="text"
              inputMode="decimal"
              value={ratioStr}
              onChange={(e) => { setRatioStr(e.target.value); setRatioError(null); }}
              onBlur={() => void saveRatio()}
              onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
            />
            {ratioError && (
              <div style={{ padding: '6px var(--space-lg) 8px', fontSize: 'var(--font-sm)', color: 'var(--tg-destructive, #ff3b30)' }}>
                {ratioError}
              </div>
            )}
            <Input
              label="Мин. bid, USDT (0 — без фильтра)"
              type="text"
              inputMode="decimal"
              value={minBidStr}
              onChange={(e) => { setMinBidStr(e.target.value); setMinBidError(null); }}
              onBlur={() => void saveMinBid()}
              onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
            />
            {minBidError && (
              <div style={{ padding: '6px var(--space-lg) 8px', fontSize: 'var(--font-sm)', color: 'var(--tg-destructive, #ff3b30)' }}>
                {minBidError}
              </div>
            )}
          </>
        )}
      </Section>

      <Section
        header="Отслеживаемые активы"
        footer="Опрашиваются все страйки и экспирации каждые 30 секунд. Новые добавляются автоматически. Нажмите на тикер, чтобы скопировать."
      >
        {prefsError && (
          <div style={{ padding: 'var(--space-sm) var(--space-lg)', fontSize: 'var(--font-sm)', color: 'var(--tg-destructive)' }}>
            {prefsError}
          </div>
        )}
        {underlyings.length === 0 ? (
          <Cell title="…" />
        ) : (
          <>
            <div
              style={{
                padding: 'var(--space-md) var(--space-lg)',
                display: 'flex',
                flexWrap: 'wrap',
                gap: 'var(--space-xs)',
              }}
            >
              {underlyings.filter((t) => !disabledUnderlyings.includes(t)).map((ticker) => (
                <div key={ticker} style={{ display: 'inline-flex', gap: 4 }}>
                  <button
                    type="button"
                    onClick={() => copyTicker(ticker)}
                    style={{
                      fontSize: '11px',
                      lineHeight: 1.3,
                      padding: 'var(--space-xs) var(--space-sm)',
                      borderRadius: 'var(--radius-sm)',
                      border: '0.5px solid var(--tg-separator)',
                      background: copiedTicker === ticker ? 'var(--tg-button)' : 'var(--tg-secondary-bg)',
                      color: copiedTicker === ticker ? 'var(--tg-button-text)' : 'var(--tg-text)',
                      cursor: 'pointer',
                      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                    }}
                  >
                    {copiedTicker === ticker ? `✓ ${ticker}` : ticker}
                  </button>
                  <button type="button" onClick={() => setUnderlyingEnabled(ticker, false)} style={{ border: 'none', background: 'transparent', color: 'var(--tg-destructive)', cursor: 'pointer' }}>×</button>
                </div>
              ))}
            </div>
            {disabledUnderlyings.length > 0 && (
              <div style={{ padding: '0 var(--space-lg) var(--space-md)', color: 'var(--tg-hint)', fontSize: 'var(--font-sm)' }}>
                Отключённые:
                <div style={{ marginTop: 6, display: 'flex', flexWrap: 'wrap', gap: 'var(--space-xs)' }}>
                  {disabledUnderlyings.map((ticker) => (
                    <span key={`disabled-${ticker}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, border: '0.5px solid var(--tg-separator)', borderRadius: 'var(--radius-sm)', padding: '2px 8px' }}>
                      {ticker}
                      <button type="button" onClick={() => setUnderlyingEnabled(ticker, true)} style={{ border: 'none', background: 'transparent', color: 'var(--tg-link)', cursor: 'pointer' }}>+</button>
                    </span>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </Section>

      {/* Табы: Срабатывания / История проверок / Снапшот */}
      <div
        style={{
          display: 'flex',
          margin: '0 var(--space-md) var(--space-md)',
          borderRadius: 'var(--radius-md)',
          overflow: 'hidden',
          border: '0.5px solid var(--tg-separator)',
        }}
      >
        {(['alerts', 'history', 'snapshot'] as const).map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTab(t)}
            style={{
              flex: 1,
              minHeight: 40,
              border: 'none',
              background: tab === t ? 'var(--tg-button)' : 'transparent',
              color: tab === t ? 'var(--tg-button-text)' : 'var(--tg-text)',
              fontSize: 'var(--font-md)',
              cursor: 'pointer',
              fontWeight: tab === t ? 600 : 400,
            }}
          >
            {t === 'alerts' ? 'Срабатывания' : t === 'history' ? 'История проверок' : 'Снапшот'}
          </button>
        ))}
      </div>

      {tab === 'alerts' ? (
        <Section
          header="Срабатывания"
          footer="Bid страйка стал ≥ N × минимальный ask за последние 10 минут (ask = 0 пропускается). После срабатывания — пауза 10 мин по этому страйку."
        >
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, padding: '8px var(--space-lg)', borderBottom: '0.5px solid var(--tg-separator)' }}>
            <button
              type="button"
              onClick={() => toggleExcludeNearExpiry(!excludeNearExpiry)}
              style={{
                border: '0.5px solid var(--tg-separator)',
                borderRadius: 'var(--radius-sm)',
                background: excludeNearExpiry ? 'var(--tg-button)' : 'var(--tg-secondary-bg)',
                color: excludeNearExpiry ? 'var(--tg-button-text)' : 'var(--tg-text)',
                padding: '6px 10px',
                cursor: 'pointer',
              }}
            >
              {excludeNearExpiry ? '✓ ' : ''}Исключить экспирацию сегодня/завтра
            </button>
            <button type="button" onClick={selectLast3Minutes} style={{ border: '0.5px solid var(--tg-separator)', borderRadius: 'var(--radius-sm)', background: 'var(--tg-secondary-bg)', color: 'var(--tg-text)', padding: '6px 10px', cursor: 'pointer' }}>
              Выбрать все опционы со срабатыванием за последние 3 минуты
            </button>
            {selectedAlertIds.size > 0 && (
              <button type="button" onClick={() => void submitSelectedOrders()} disabled={batchOrdering} style={{ border: 'none', borderRadius: 'var(--radius-sm)', background: 'var(--tg-button)', color: 'var(--tg-button-text)', padding: '6px 10px', cursor: batchOrdering ? 'default' : 'pointer' }}>
                {batchOrdering ? 'Выставление…' : `Выставить заявки (${selectedAlertIds.size})`}
              </button>
            )}
          </div>
          {alerts.error && (
            <Cell before="⚠️" title="Не удалось загрузить срабатывания" subtitle={alerts.error} />
          )}
          {historyStale && (
            <Cell
              before="⏳"
              title="Проверки опционов не выполняются"
              subtitle={`Последняя проверка ${Math.round(historyAgeMs / 60_000)} мин назад — новые срабатывания не появятся, пока poller-options (cron) или worker не запущены.`}
            />
          )}
          {alerts.isLoading ? (
            <Spinner />
          ) : !filteredAlerts.length ? (
            <Empty icon="⚡" title="Пока тихо" />
          ) : (
            <>
              {filteredAlerts.map((a) => (
                <div key={a.id}>
                  {(() => {
                    const position = positionsBySymbol[a.symbol];
                    const positionSuffix = position
                      ? ` · my qty ${fmtValue(position.qty, true)}${position.avgEntryPrice != null ? ` · avg ${fmtValue(position.avgEntryPrice)}` : ''}`
                      : '';
                    return (
                      <Cell
                        before={<input type="checkbox" checked={selectedAlertIds.has(a.id)} onChange={() => toggleAlertSelection(a.id)} />}
                        onClick={() => openOrderModal(a)}
                        title={
                          <span style={{ color: 'var(--tg-link)' }}>
                            #{a.symbol}
                          </span>
                        }
                        subtitle={`ask ${a.prev_ask} (${fmtDateTime(a.prev_ask_at)}) → bid ${a.new_bid} · qty ${fmtValue(a.best_bid_size, true)} · ×${a.ratio.toFixed(2)}${positionSuffix}`}
                      />
                    );
                  })()}
                  {selectedAlertIds.has(a.id) && (
                    <div style={{ padding: '0 var(--space-lg) 8px', color: 'var(--tg-hint)', fontSize: 'var(--font-sm)' }}>
                      Открыть лимитную заявку: bid · Price {(a.new_bid * PREFILLED_ORDER_FACTOR).toFixed(6)} · Qty {fmtValue(pickOrderQty(positionsBySymbol[a.symbol]?.qty ?? null, positiveOrNull(a.best_bid_size)), true)}
                      <button
                        type="button"
                        onClick={() => void placePreFilledOrder(a)}
                        disabled={batchOrderState[a.id]?.status === 'pending'}
                        style={{ marginLeft: 8, border: '0.5px solid var(--tg-separator)', borderRadius: 'var(--radius-sm)', background: 'var(--tg-secondary-bg)', color: 'var(--tg-text)', padding: '4px 8px', cursor: batchOrderState[a.id]?.status === 'pending' ? 'default' : 'pointer' }}
                      >
                        Выставить
                      </button>
                      {batchOrderState[a.id] && (
                        <span style={{ marginLeft: 8, color: batchOrderState[a.id].status === 'success' ? '#34c759' : batchOrderState[a.id].status === 'error' ? 'var(--tg-destructive)' : 'var(--tg-hint)' }}>
                          {batchOrderState[a.id].message}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              ))}
              {selectedAlert && (
                <div style={{ padding: 'var(--space-md) var(--space-lg)', borderTop: '0.5px solid var(--tg-separator)' }}>
                  <div style={{ fontSize: 'var(--font-md)', color: 'var(--tg-text)', marginBottom: 8 }}>
                    Лимитная заявка для #{selectedAlert.symbol}
                  </div>
                  <div style={{ display: 'flex', gap: 12, marginBottom: 8, color: 'var(--tg-text)', fontSize: 'var(--font-sm)' }}>
                    <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                      <input type="radio" name="gamma-side" checked={orderSide === 'bid'} onChange={() => setOrderSide('bid')} />
                      bid
                    </label>
                    <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                      <input type="radio" name="gamma-side" checked={orderSide === 'ask'} onChange={() => setOrderSide('ask')} />
                      ask
                    </label>
                  </div>
                  <Input
                    label="Количество"
                    type="text"
                    inputMode="decimal"
                    value={orderQty}
                    onChange={(e) => setOrderQty(e.target.value.replace(/,/g, '.'))}
                  />
                  <Input
                    label="Цена"
                    type="text"
                    inputMode="decimal"
                    value={orderPrice}
                    onChange={(e) => setOrderPrice(e.target.value.replace(/,/g, '.'))}
                  />
                  <div style={{ marginTop: 8, color: 'var(--tg-hint)', fontSize: 'var(--font-sm)' }}>
                    Текущие цены: bid {fmtValue(latestQuote?.bid ?? selectedAlert.new_bid)} · ask {fmtValue(latestQuote?.ask ?? selectedAlert.prev_ask)} · позиция {fmtValue(positionsBySymbol[selectedAlert.symbol]?.qty, true)}
                  </div>
                  <div style={{ marginTop: 6, color: 'var(--tg-hint)', fontSize: 'var(--font-sm)' }}>
                    Рекомендовано для {orderSide}: {fmtValue((orderSide === 'bid'
                      ? (latestQuote?.bid ?? selectedAlert.new_bid)
                      : (latestQuote?.ask ?? selectedAlert.prev_ask)
                    ) * ORDER_PRICE_DISCOUNT_FACTOR)}
                  </div>
                  <div style={{ marginTop: 4, color: 'var(--tg-hint)', fontSize: 'var(--font-sm)' }}>
                    Фактическая цена отправки: {fmtValue(Number(orderPrice.replace(',', '.')))}
                  </div>
                  <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                    <button
                      type="button"
                      onClick={() => void submitOrder()}
                      disabled={orderLoading}
                      style={{
                        border: 'none',
                        borderRadius: 'var(--radius-sm)',
                        background: 'var(--tg-button)',
                        color: 'var(--tg-button-text)',
                        padding: '8px 12px',
                        cursor: orderLoading ? 'default' : 'pointer',
                      }}
                    >
                      {orderLoading ? 'Отправка…' : 'Открыть лимитную заявку'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setSelectedAlert(null)}
                      style={{
                        border: '0.5px solid var(--tg-separator)',
                        borderRadius: 'var(--radius-sm)',
                        background: 'var(--tg-secondary-bg)',
                        color: 'var(--tg-text)',
                        padding: '8px 12px',
                        cursor: 'pointer',
                      }}
                    >
                      Закрыть
                    </button>
                  </div>
                  {orderError && <div style={{ marginTop: 8, color: 'var(--tg-destructive)', fontSize: 'var(--font-sm)' }}>{orderError}</div>}
                  {orderResult && <div style={{ marginTop: 8, color: '#34c759', fontSize: 'var(--font-sm)' }}>{orderResult}</div>}
                </div>
              )}
            </>
          )}
        </Section>
      ) : tab === 'history' ? (
        <Section
          header="История проверок"
          footer="Каждые 30 секунд poller-options опрашивает все активные опционы. Здесь отображается история этих проверок."
        >
          {history.error && (
            <Cell before="⚠️" title="Не удалось загрузить историю" subtitle={history.error} />
          )}
          {historyStale && (
            <Cell
              before="⏳"
              title="История не обновляется"
              subtitle={`Последняя проверка ${Math.round(historyAgeMs / 60_000)} мин назад (${fmtDateTime(lastCheckAt)}). Проверьте, что poller-options (cron) или worker запущены.`}
            />
          )}
          {history.isLoading ? (
            <Spinner />
          ) : !history.data?.length ? (
            history.error ? null : <Empty icon="📋" title="Нет записей" hint="Записи появятся после первого запуска poller-options." />
          ) : (
            history.data.map((h) => (
              <Cell
                key={h.id}
                before={h.status === 'ok' ? '✅' : '❌'}
                title={fmtDateTime(h.checked_at)}
                subtitle={
                  h.status === 'ok'
                    ? `${h.underlyings_count} активов · ${h.tickers_count} тикеров · ${h.quotes_count} котировок · ${h.alerts_count} срабат.${h.max_observed_ratio != null ? ` · max ratio ×${h.max_observed_ratio.toFixed(2)}` : ''} · ${h.duration_ms} мс`
                    : `Ошибка: ${h.error_message ?? '—'}`
                }
              />
            ))
          )}
        </Section>
      ) : (
        <>
          <Section header="Снапшот — актив" footer="Выберите underlying, чтобы получить live-срез котировок с Gate.io.">
            {!underlyings.length ? (
              <Cell title="…" />
            ) : (
              <div
                style={{
                  padding: 'var(--space-md) var(--space-lg)',
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 'var(--space-xs)',
                }}
              >
                {underlyings.map((ticker) => (
                  <button
                    key={`snapshot-${ticker}`}
                    type="button"
                    onClick={() => {
                      setSnapshotUnderlying(ticker);
                      setSnapshotExpiry(null);
                      setSnapshotData(null);
                      setSnapshotError(null);
                    }}
                    style={{
                      fontSize: '11px',
                      lineHeight: 1.3,
                      padding: 'var(--space-xs) var(--space-sm)',
                      borderRadius: 'var(--radius-sm)',
                      border: '0.5px solid var(--tg-separator)',
                      background: snapshotUnderlying === ticker ? 'var(--tg-button)' : 'var(--tg-secondary-bg)',
                      color: snapshotUnderlying === ticker ? 'var(--tg-button-text)' : 'var(--tg-text)',
                      cursor: 'pointer',
                      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                      transition: 'background 0.15s, color 0.15s',
                      userSelect: 'none',
                      flexShrink: 0,
                    }}
                  >
                    {ticker}
                  </button>
                ))}
              </div>
            )}
          </Section>

          {snapshotUnderlying && (
            <Section
              header="Снапшот — экспирация"
              footer={`Последнее обновление: ${snapshotData?.fetched_at ? fmtDateTime(snapshotData.fetched_at) : '—'}`}
            >
              <div
                style={{
                  padding: 'var(--space-sm) var(--space-lg)',
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 'var(--space-xs)',
                  borderBottom: '0.5px solid var(--tg-separator)',
                }}
              >
                {(snapshotData?.expiries ?? []).map((expiry) => (
                  <button
                    key={expiry}
                    type="button"
                    onClick={() => setSnapshotExpiry(expiry)}
                    style={{
                      padding: '4px 10px',
                      borderRadius: 'var(--radius-sm)',
                      border: '0.5px solid var(--tg-separator)',
                      background: snapshotExpiry === expiry ? 'var(--tg-button)' : 'var(--tg-secondary-bg)',
                      color: snapshotExpiry === expiry ? 'var(--tg-button-text)' : 'var(--tg-text)',
                      cursor: 'pointer',
                      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                      fontSize: 'var(--font-sm)',
                    }}
                  >
                    {expiry}
                  </button>
                ))}
              </div>

              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 'var(--space-md)',
                  padding: 'var(--space-sm) var(--space-lg)',
                  borderBottom: '0.5px solid var(--tg-separator)',
                }}
              >
                <button
                  type="button"
                  onClick={refreshSnapshot}
                  disabled={!snapshotExpiry || snapshotLoading}
                  style={{
                    border: '0.5px solid var(--tg-separator)',
                    borderRadius: 'var(--radius-sm)',
                    background: 'var(--tg-secondary-bg)',
                    color: 'var(--tg-text)',
                    padding: '6px 12px',
                    cursor: snapshotExpiry ? 'pointer' : 'default',
                  }}
                >
                  Обновить
                </button>
                <label
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 'var(--space-xs)',
                    color: 'var(--tg-hint)',
                    fontSize: 'var(--font-sm)',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={autoRefresh}
                    onChange={(e) => setAutoRefresh(e.target.checked)}
                    disabled={!snapshotExpiry}
                  />
                  Авто-обновление каждые 10 сек
                </label>
              </div>

              {/* Спиннер — только пока нет данных по выбранной экспирации; авто-обновление не прячет таблицу. */}
              {snapshotLoading && (!snapshotData || snapshotData.selected_expiry !== snapshotExpiry) ? (
                <Spinner />
              ) : snapshotError ? (
                <Empty icon="⚠️" title="Ошибка снапшота" hint={snapshotError} />
              ) : !snapshotExpiry ? (
                <Empty icon="📅" title="Выберите экспирацию" />
              ) : strikeRows.length === 0 ? (
                <Empty icon="📉" title="Нет котировок" hint="По выбранной экспирации нет активных страйков." />
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table
                    style={{
                      width: '100%',
                      minWidth: 640,
                      borderCollapse: 'collapse',
                      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                      fontSize: 'var(--font-sm)',
                    }}
                  >
                    <thead>
                      <tr style={{ color: 'var(--tg-hint)' }}>
                        <th style={{ textAlign: 'left', padding: '8px 10px' }}>Страйк</th>
                        <th style={{ textAlign: 'left', padding: '8px 10px' }}>Call bid / ask (size)</th>
                        <th style={{ textAlign: 'left', padding: '8px 10px' }}>Put bid / ask (size)</th>
                        <th style={{ textAlign: 'left', padding: '8px 10px' }}>IV</th>
                        <th style={{ textAlign: 'left', padding: '8px 10px' }}>Δ</th>
                      </tr>
                    </thead>
                    <tbody>
                      {strikeRows.map((row) => (
                        <tr key={row.strike} style={{ borderTop: '0.5px solid var(--tg-separator)', color: 'var(--tg-text)' }}>
                          <td style={{ padding: '8px 10px', whiteSpace: 'nowrap' }}>{row.strike}</td>
                          <td style={{ padding: '8px 10px', whiteSpace: 'nowrap' }}>
                            {row.call
                              ? `${fmtValue(row.call.bid, true)} / ${fmtValue(row.call.ask, true)} (${fmtValue(row.call.bid_size)} / ${fmtValue(row.call.ask_size)})`
                              : '—'}
                          </td>
                          <td style={{ padding: '8px 10px', whiteSpace: 'nowrap' }}>
                            {row.put
                              ? `${fmtValue(row.put.bid, true)} / ${fmtValue(row.put.ask, true)} (${fmtValue(row.put.bid_size)} / ${fmtValue(row.put.ask_size)})`
                              : '—'}
                          </td>
                          <td style={{ padding: '8px 10px', whiteSpace: 'nowrap' }}>
                            C {fmtValue(row.call?.mark_iv ?? null)} · P {fmtValue(row.put?.mark_iv ?? null)}
                          </td>
                          <td style={{ padding: '8px 10px', whiteSpace: 'nowrap' }}>
                            C {fmtValue(row.call?.delta ?? null)} · P {fmtValue(row.put?.delta ?? null)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Section>
          )}
        </>
      )}
    </Page>
  );
}
